import React, { useRef, useState } from 'react';
import numeral from 'numeral';

/* ─────────────────────────────────────────────
   Keyframe injection — scoped to this component
   (avoids adding to global CSS bundle)
───────────────────────────────────────────── */
const TICKER_STYLE_ID = 'succession-marketplace-ticker-styles';

function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TICKER_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = TICKER_STYLE_ID;
  el.textContent = `
    @keyframes succession-ticker-scroll {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .succession-ticker-track {
      display: flex;
      width: max-content;
      animation: succession-ticker-scroll 40s linear infinite;
    }
    .succession-ticker-track:hover,
    .succession-ticker-paused .succession-ticker-track {
      animation-play-state: paused;
    }
    .succession-ticker-pulse {
      animation: succession-ticker-pulse-dot 2s ease-in-out infinite;
    }
    @keyframes succession-ticker-pulse-dot {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.3; }
    }
  `;
  document.head.appendChild(el);
}

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function formatValue(ticker, locale) {
  const fmt = locale === 'fr' ? (ticker.french_format || ticker.format) : ticker.format;
  try {
    return numeral(ticker.value).format(fmt);
  } catch (_) {
    return String(ticker.value ?? '—');
  }
}

function getLabel(ticker, locale) {
  return locale === 'fr' ? (ticker.french_label || ticker.label) : ticker.label;
}

function getValueColor(ticker) {
  if (!ticker.arrow) return null; // neutral — inherit
  const n = Number(ticker.value);
  if (n > 0) return 'var(--succession-success, #22c55e)';
  if (n < 0) return 'var(--succession-error, #ef4444)';
  return null;
}

function ArrowIcon({ value }) {
  const n = Number(value);
  if (n === 0 || isNaN(n)) return null;
  const up = n > 0;
  return (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="currentColor"
      aria-hidden="true"
      style={{ marginRight: 2, flexShrink: 0, verticalAlign: 'middle' }}
    >
      {up
        ? <path d="M4 1 L7 6 L1 6 Z" />
        : <path d="M4 7 L7 2 L1 2 Z" />}
    </svg>
  );
}

/* ─────────────────────────────────────────────
   Single ticker chip
───────────────────────────────────────────── */
function TickerChip({ ticker, locale }) {
  const label      = getLabel(ticker, locale);
  const formatted  = formatValue(ticker, locale);
  const valueColor = getValueColor(ticker);

  return (
    <div
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        gap:            6,
        padding:        '0 18px',
        height:         '100%',
        borderRight:    '1px solid var(--succession-border)',
        flexShrink:     0,
        whiteSpace:     'nowrap',
      }}
    >
      {/* Color dot (uses ticker.color if present, else value-color, else muted) */}
      <span
        style={{
          width:        6,
          height:       6,
          borderRadius: '50%',
          flexShrink:   0,
          backgroundColor: ticker.color
            || valueColor
            || 'var(--succession-text-muted, #94a3b8)',
        }}
        aria-hidden="true"
      />

      {/* Label */}
      <span
        style={{
          fontSize:      10,
          fontWeight:    700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color:         'var(--succession-text-secondary)',
          fontFamily:    'inherit',
        }}
      >
        {label}
      </span>

      {/* Divider */}
      <span
        style={{
          width:           1,
          height:          12,
          backgroundColor: 'var(--succession-border)',
          flexShrink:      0,
        }}
        aria-hidden="true"
      />

      {/* Value + arrow */}
      <span
        style={{
          display:    'inline-flex',
          alignItems: 'center',
          fontSize:   12,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          fontFamily: '"SF Mono", "Fira Mono", "Cascadia Code", monospace',
          color:      valueColor || 'var(--succession-text-primary)',
          gap:        2,
        }}
      >
        {ticker.arrow && <ArrowIcon value={ticker.value} />}
        {formatted}
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
export default function SuccessionMarketplaceTicker({ tickers = [], locale = 'en' }) {
  injectStyles();

  const [paused, setPaused] = useState(false);
  const wrapperRef = useRef(null);

  if (!tickers || tickers.length === 0) return null;

  // Duplicate items for seamless infinite loop
  const doubled = [...tickers, ...tickers];

  return (
    <div
      ref={wrapperRef}
      className={`succession-ticker-paused-wrapper${paused ? ' succession-ticker-paused' : ''}`}
      aria-label="Marketplace live metrics ticker"
      role="marquee"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position:        'relative',
        width:           '100%',
        overflow:        'hidden',
        borderTop:       '1px solid var(--succession-border)',
        borderBottom:    '1px solid var(--succession-border)',
        backgroundColor: 'var(--succession-bg-primary)',
        height:          40,
        display:         'flex',
        alignItems:      'center',
        userSelect:      'none',
      }}
    >
      {/* ── Left label badge (non-scrolling) ── */}
      <div
        style={{
          flexShrink:      0,
          display:         'flex',
          alignItems:      'center',
          gap:             6,
          padding:         '0 12px 0 14px',
          height:          '100%',
          borderRight:     '1px solid var(--succession-border)',
          backgroundColor: 'var(--succession-bg-primary)',
          zIndex:          2,
          position:        'relative',
        }}
      >
        {/* Pulsing dot */}
        <span
          className="succession-ticker-pulse"
          style={{
            width:           6,
            height:          6,
            borderRadius:    '50%',
            backgroundColor: 'var(--succession-success, #22c55e)',
            flexShrink:      0,
          }}
          aria-hidden="true"
        />
        <span
          style={{
            fontSize:      9,
            fontWeight:    800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color:         'var(--succession-text-secondary)',
            fontFamily:    '"SF Mono", "Fira Mono", monospace',
          }}
        >
          Marketplace
        </span>
      </div>

      {/* ── Left fade mask ── */}
      <div
        aria-hidden="true"
        style={{
          position:   'absolute',
          left:       /* width of badge */ 0,
          top:        0,
          bottom:     0,
          width:      40,
          background: 'linear-gradient(to right, var(--succession-bg-primary) 0%, transparent 100%)',
          zIndex:     1,
          pointerEvents: 'none',
          // Shift right of the badge
          marginLeft: 'calc(var(--ticker-badge-w, 110px))',
        }}
      />

      {/* ── Scrolling track ── */}
      <div
        style={{
          flex:     1,
          overflow: 'hidden',
          height:   '100%',
          position: 'relative',
        }}
      >
        <div
          className={`succession-ticker-track${paused ? ' succession-ticker-paused' : ''}`}
          style={{ height: '100%', alignItems: 'center' }}
        >
          {doubled.map((ticker, i) => (
            <TickerChip key={`${ticker.label}-${i}`} ticker={ticker} locale={locale} />
          ))}
        </div>
      </div>

      {/* ── Right fade mask ── */}
      <div
        aria-hidden="true"
        style={{
          position:      'absolute',
          right:         0,
          top:           0,
          bottom:        0,
          width:         48,
          background:    'linear-gradient(to left, var(--succession-bg-primary) 0%, transparent 100%)',
          zIndex:        1,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
